import React from 'react'
import { ActivityIndicator } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { createNativeStackNavigator } from '@react-navigation/native-stack'
import { Screen } from '@components'
import { useLanguage } from '@context'
import { useAdminMenu } from '@hooks'
import { AccessDenied } from '../../screens/menu/components/AccessDenied'
import {
  AdminAddOrganizer,
  AdminEventDetail,
  AdminEvents,
  AdminHome,
  AdminOrganizerDetail,
  AdminOrganizers,
  AdminRequests,
  AdminSettings,
} from '../../screens/menu/admin'
import type { AdminOrganizerStatus } from '@hooks'

export type AdminStackParamList = {
  AdminHome: undefined
  AdminRequests: undefined
  AdminOrganizers: { status?: AdminOrganizerStatus } | undefined
  AdminOrganizerDetail: { accountId: string }
  AdminAddOrganizer: undefined
  AdminEvents: Record<string, string | undefined> | undefined
  AdminEventDetail: { eventId: string }
  AdminSettings: undefined
}

const Stack = createNativeStackNavigator<AdminStackParamList>()

/** Super-admin panel: overview, requests, organizers, events and platform settings. */
const AdminStackNavigator: React.FC = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const navigation = useNavigation<any>()
  const { isSuperAdmin, isLoading } = useAdminMenu()

  // The API already refuses every admin action; the screens must not even open
  // for anyone else (typed URL, old bookmark).
  if (isLoading) {
    return (
      <Screen>
        <ActivityIndicator color={theme.colors.primary} />
      </Screen>
    )
  }
  if (!isSuperAdmin) {
    return (
      <AccessDenied
        testID="admin-access-denied"
        message={t('admin.denied')}
        onBack={() => navigation.navigate('MenuMain')}
      />
    )
  }
  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="AdminHome" component={AdminHome} />
      <Stack.Screen name="AdminRequests" component={AdminRequests} />
      <Stack.Screen name="AdminOrganizers" component={AdminOrganizers} />
      <Stack.Screen name="AdminOrganizerDetail" component={AdminOrganizerDetail} />
      <Stack.Screen name="AdminAddOrganizer" component={AdminAddOrganizer} />
      <Stack.Screen name="AdminEvents" component={AdminEvents} />
      <Stack.Screen name="AdminEventDetail" component={AdminEventDetail} />
      <Stack.Screen name="AdminSettings" component={AdminSettings} />
    </Stack.Navigator>
  )
}

export default AdminStackNavigator
